import { PlaceAutocomplete } from "@/components/others/place-autocomplete";
import type { PlaceDetails } from "@/components/others/place-autocomplete/types";
import { projectTypes } from "@/info/project-types";
import { useSearchPageLocationsStore } from "@/providers/search";
import { Filters, LocationType } from "@/types/filters";
import { storeLocationDetails } from "@/utils/location-details-store";

type FilterLocation = Filters["locations"][number];

interface PlaceAutocompleteWrapperProps {
	value: FilterLocation[];
	onChange: (locations: FilterLocation[]) => void;
	showChips?: boolean;
	chipsPosition?: "above" | "below";
	autoFocus?: boolean;
	placeholder?: string;
	className?: string;
	filterField?: boolean;
	restrictions?: {
		includedPrimaryTypes?: string[];
		language?: string;
		radius?: number;
		region?: string;
	};
}

export function PlaceAutocompleteWrapper({
	value,
	onChange,
	showChips = true,
	chipsPosition = "above",
	autoFocus = false,
	placeholder = "Search locations or projects...",
	className = "w-full",
	filterField = true,
	restrictions = {
		includedPrimaryTypes: ["locality", "sublocality", "establishment"],
		language: "en",
		radius: 30,
		region: "IN",
	},
}: PlaceAutocompleteWrapperProps) {
	const locationsStore = useSearchPageLocationsStore();

	const handlePlaceSelect = (place: PlaceDetails) => {
		// console.log(place, "place");
		const existingLocations = value || [];

		// Extract coordinates properly - handle both function and number types
		let latitude: number | undefined;
		let longitude: number | undefined;

		if (place.location) {
			const lat = place.location.lat as number | (() => number);
			const lng = place.location.lng as number | (() => number);

			// Handle both function and number types from Google Places API
			latitude = typeof lat === "function" ? lat() : lat !== undefined ? Number(lat) : undefined;
			longitude = typeof lng === "function" ? lng() : lng !== undefined ? Number(lng) : undefined;

			// Ensure we have valid numbers
			if (latitude === undefined || longitude === undefined || isNaN(latitude) || isNaN(longitude)) {
				latitude = undefined;
				longitude = undefined;
			}
		}

		const newLocation = {
			id: place.id || "", // Use place.id which should contain placeId from GraphQL response
			name: place.displayName,
			type: (projectTypes.includes(place.types?.[0] || "") ? "project" : "landmark") as LocationType,
			coordinates:
				latitude !== undefined && longitude !== undefined
					? {
							latitude,
							longitude,
						}
					: undefined,
		};

		// Check if location already exists to prevent duplicates
		// Check both by ID and by name to catch all potential duplicates
		const locationExists = existingLocations.some(
			loc => loc.id === newLocation.id || loc.name.toLowerCase().trim() === newLocation.name.toLowerCase().trim(),
		);

		if (locationExists) {
			return;
		}

		// Update both filters and locations store
		const updatedLocations = [...existingLocations, newLocation];
		onChange(updatedLocations);

		// Store location details for later retrieval from URL
		storeLocationDetails(newLocation);

		// Also update the locations store
		locationsStore.getState().addLocation(newLocation);
	};

	const handleItemRemove = (itemId: string) => {
		const existingLocations = value || [];
		onChange(existingLocations.filter(loc => loc.id !== itemId));
	};

	const selectedItems = (value || []).map(loc => ({
		id: loc.id,
		displayName: loc.name,
		formattedAddress: loc.name,
		location: loc.coordinates
			? {
					lat: Number(loc.coordinates.latitude),
					lng: Number(loc.coordinates.longitude),
				}
			: undefined,
	}));

	return (
		<PlaceAutocomplete
			onPlaceSelect={handlePlaceSelect}
			selectedItems={selectedItems}
			onItemRemove={handleItemRemove}
			showChips={showChips}
			chipsPosition={chipsPosition}
			placeholder={placeholder}
			filterField={filterField}
			restrictions={restrictions}
			autoFocus={autoFocus}
			inputProps={{ className }}
		/>
	);
}
